/**
 * Helpers for Data Collection label/entity extraction UX: whether a target
 * is still scanning a file, and whether mapped values sit outside the task
 * definition (the Select Label / Select Entity warning icon).
 */

/** True while columns are still being read or distinct labels are in flight. */
export function isLabelExtractionPending({ files, details } = {}) {
    if (details?.hasLabels === false) return false;
    const list = files || [];
    if (!list.length) return false;
    if (list.some((entry) => entry?.columnsLoading)) return true;
    return Boolean(details?.labelsLoading);
}

/**
 * Extracted values whose Replace mapping is not in the Business Goals catalog.
 * Dropped rows (`mapping === null`) are ignored — they no longer appear in
 * Select Label / Select Entity.
 */
export function unknownMappedLabels(details, availableLabels) {
    if (!details || details.hasLabels === false) return [];
    const available = new Set(availableLabels || []);
    const mappings = details.labelMappings || {};
    return (details.extractedLabels || []).filter((label) => {
        if (mappings[label] === null) return false;
        const mapped = mappings[label] ?? label;
        return !available.has(mapped);
    });
}

const NO_MISMATCH = Object.freeze({ missing: [], unknown: [], pending: false, settled: false });

/**
 * Catalog mismatch of one details form, the input of the warning banner with
 * the Fix menu: task labels/entities no Replace-or-create value covers
 * (`missing`, minus the form's fallback label) and file values the task does
 * not define (`unknown`). Dropped rows (`mapping === null`) count for neither.
 * Nothing is judged until extraction has settled: `pending` while labels or
 * columns are still loading, `settled` once values exist to compare.
 */
export function catalogMismatchForDetails(details, availableLabels, { columnsLoading = false } = {}) {
    if (!details || details.hasLabels === false) return NO_MISMATCH;
    const extracted = details.extractedLabels || [];
    const pending = Boolean(details.labelsLoading || columnsLoading);
    if (pending || !extracted.length) return { ...NO_MISMATCH, pending };

    const mappings = details.labelMappings || {};
    const mappedValues = new Set(
        extracted.filter((label) => mappings[label] !== null).map((label) => mappings[label] ?? label)
    );
    const catalog = new Set(availableLabels || []);
    const missing = (availableLabels || []).filter(
        (label) => !mappedValues.has(label) && label !== details.rejectionLabel
    );
    const unknown = extracted.filter((label) => {
        if (mappings[label] === null) return false;
        const mapped = mappings[label] ?? label;
        return !catalog.has(label) && !catalog.has(mapped);
    });
    return { missing, unknown, pending: false, settled: true };
}

/**
 * One mismatch for several files of the same kind (all uploaded datasets, or
 * all benchmarks): a task label is missing only when no file covers it, and
 * the unknown values are the union across files. Empty while any file is
 * still extracting, so the merged banner does not flicker file by file.
 *
 * @param {Array<{details: object, columnsLoading?: boolean}>} entries
 */
export function mergedCatalogMismatch(entries, availableLabels) {
    const perFile = (entries || []).map(({ details, columnsLoading }) =>
        catalogMismatchForDetails(details, availableLabels, { columnsLoading })
    );
    if (perFile.some((mismatch) => mismatch.pending)) return { missing: [], unknown: [] };
    const settled = perFile.filter((mismatch) => mismatch.settled);
    if (!settled.length) return { missing: [], unknown: [] };
    const missing = settled[0].missing.filter((label) => settled.every((mismatch) => mismatch.missing.includes(label)));
    const unknown = [...new Set(settled.flatMap((mismatch) => mismatch.unknown))];
    return { missing, unknown };
}

/**
 * Text of the Data Collection catalog-mismatch banner (the one with the Fix
 * menu): task labels/entities no file value covers, file labels/entities the
 * task does not define, or both. Empty when neither applies.
 */
export function labelCatalogBannerText({
    missingLabels = [],
    unknownLabels = [],
    isNer = false,
} = {}) {
    const parts = [];
    if (missingLabels.length) {
        parts.push(
            `Missing ${isNer ? 'Entity' : 'Label'} as defined in task creation: ${missingLabels.join(', ')}`
        );
    }
    if (unknownLabels.length) {
        parts.push(
            `${isNer ? 'Entities' : 'Labels'} in file not defined in task creation: ${unknownLabels.join(', ')}`
        );
    }
    return parts.join('. ');
}
